import { useState, useEffect, useRef } from "react";
import { Megaphone, Plus, Trash2, Upload, Loader2, GripVertical, X, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection from "./ConfigSection";
import { supabase } from "@/integrations/supabase/client";

interface PromoPopup {
  id: string;
  title: string;
  description: string;
  image_url: string;
  is_active: boolean;
  show_on_open: boolean;
  link_url: string;
  sort_order: number;
}

const PopupsPromo = () => {
  const { restaurant, toast } = useConfig();
  const [popups, setPopups] = useState<PromoPopup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (!restaurant) return;
    const load = async () => {
      const { data } = await supabase
        .from("promo_popups")
        .select("*")
        .eq("restaurant_id", restaurant.id)
        .order("sort_order") as any;
      setPopups(data || []);
      setLoading(false);
    };
    load();
  }, [restaurant]);

  const addPopup = async () => {
    if (!restaurant) return;
    setSaving(true);
    const { data, error } = await supabase.from("promo_popups").insert({
      restaurant_id: restaurant.id,
      title: "Nova Promoção",
      description: "",
      sort_order: popups.length,
    } as any).select().single() as any;
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      setPopups((p) => [...p, data]);
      setEditingId(data.id);
    }
    setSaving(false);
  };

  const updatePopup = async (id: string, updates: Partial<PromoPopup>) => {
    setPopups((p) => p.map((pp) => pp.id === id ? { ...pp, ...updates } : pp));
    await supabase.from("promo_popups").update(updates as any).eq("id", id);
  };

  const deletePopup = async (id: string) => {
    setPopups((p) => p.filter((pp) => pp.id !== id));
    await supabase.from("promo_popups").delete().eq("id", id);
    toast({ title: "Popup removido" });
  };

  const uploadPopupImage = async (id: string, file: File) => {
    if (!restaurant) return;
    setUploading(id);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${restaurant.id}/popup-${id}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("restaurant-images").upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from("restaurant-images").getPublicUrl(path);
      await updatePopup(id, { image_url: urlData.publicUrl });
    } catch (err: any) {
      toast({ title: "Erro no upload", description: err.message, variant: "destructive" });
    }
    setUploading(null);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <ConfigSection title="Popups & Promoções" icon={Megaphone}>
      <p className="text-xs text-muted-foreground">
        Crie banners promocionais que aparecem automaticamente quando o cliente abre o cardápio digital.
      </p>

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
        const f = e.target.files?.[0];
        if (f && editingId) uploadPopupImage(editingId, f);
        e.target.value = "";
      }} />

      <div className="space-y-3">
        {popups.map((popup) => {
          const isEditing = editingId === popup.id;
          return (
            <div key={popup.id} className={`border rounded-xl overflow-hidden transition-all ${isEditing ? "border-primary" : "border-border"}`}>
              {/* Header */}
              <div
                className="flex items-center gap-3 px-4 py-3 bg-card cursor-pointer"
                onClick={() => setEditingId(isEditing ? null : popup.id)}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                {popup.image_url && (
                  <img src={popup.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{popup.title}</p>
                  <p className="text-[11px] text-muted-foreground">{popup.is_active ? "Ativo" : "Inativo"}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); updatePopup(popup.id, { is_active: !popup.is_active }); }}
                  className={`p-1.5 rounded-lg ${popup.is_active ? "text-green-500" : "text-muted-foreground"}`}
                >
                  {popup.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deletePopup(popup.id); }}
                  className="p-1.5 rounded-lg text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {/* Expanded form */}
              {isEditing && (
                <div className="p-4 border-t border-border space-y-3 bg-secondary/30">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Título</Label>
                    <Input
                      value={popup.title}
                      onChange={(e) => updatePopup(popup.id, { title: e.target.value })}
                      className="h-9 bg-card border-border"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Descrição (opcional)</Label>
                    <Textarea
                      value={popup.description || ""}
                      onChange={(e) => updatePopup(popup.id, { description: e.target.value })}
                      className="bg-card border-border min-h-[60px]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Link (opcional)</Label>
                    <Input
                      placeholder="https://..."
                      value={popup.link_url || ""}
                      onChange={(e) => updatePopup(popup.id, { link_url: e.target.value })}
                      className="h-9 bg-card border-border"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Imagem do Banner</Label>
                    {popup.image_url ? (
                      <div className="relative rounded-xl overflow-hidden border border-border">
                        <img src={popup.image_url} alt="" className="w-full h-32 object-cover" />
                        <button
                          onClick={() => updatePopup(popup.id, { image_url: "" })}
                          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploading === popup.id}
                      onClick={() => { setEditingId(popup.id); fileRef.current?.click(); }}
                      className="gap-1.5"
                    >
                      {uploading === popup.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      {popup.image_url ? "Trocar imagem" : "Enviar imagem"}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={popup.show_on_open}
                        onChange={(e) => updatePopup(popup.id, { show_on_open: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
                    </label>
                    <span className="text-xs text-muted-foreground">Exibir ao abrir o cardápio</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addPopup}
        disabled={saving}
        className="w-full gap-2"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        Adicionar Popup
      </Button>
    </ConfigSection>
  );
};

export default PopupsPromo;
